<template>
  <contact-form-fieldset
    v-if="bundles.length && price"
    :class-content-wrapper="wrapperClass"
    :legend="texts.meetingBundlesLabel"
    :required="true"
    :tooltip="meetingBundleTooltip"
  >
    <div
      class="radio-button contact-form-radio-button"
      v-for="(bundle, index) in bundles"
      :key="`${index}-meeting-count`"
      :class="{
        'contact-form-radio-button--active': meetingCountModel === bundle.count,
      }"
      @click="meetingCountModel = bundle.count"
    >
      <input
        class="contact-form-radio-button__input"
        name="meetingCount"
        type="radio"
        v-model="meetingCountModel"
        :id="bundle.count"
        :value="bundle.count"
      />
      <label
        class="marker contact-form-radio-button__label"
        :for="bundle.count"
      >
        <span v-text="getMeetingLabel(bundle.count, bundle.price)" />
        <span
          :class="{ 'meeting-bundle-discount': bundle.count > 1 }"
          v-html="getDiscountDescription(bundle.count, bundle.price)"
        />
      </label>
    </div>
  </contact-form-fieldset>
</template>

<script>
import {API_ROLES, hasTherapyRole,
  MEETING_BUNDLE_COUNT, 
  MEETING_TYPE_KEYS,
} from 'Main/contact-form/constants';
import { sprintf } from 'sprintf-js';
import ContactFormFieldset from './ContactFormFieldset.vue';
export default {

  components: {
    ContactFormFieldset,
  },

  computed: {
    meetingBundleTooltip() {
      return theme_vars?.tooltip[this.requestType]?.meetingBundle;
    },

    meetingCountModel: {
      get() {
        return this.meetingCount;
      },
      set(value) {
        this.$emit('update:meetingCount', value);
      },
    },

    price() {
      return this.calculatePrice();
    },

    wrapperClass() {
      let classes = 'meeting-bundles contact-form__radio-buttons-vertical';

      if (this.hasError) {
        classes += ' contact-form__radio-buttons-vertical--error';
      }

      return classes;
    },
  },

  data() {
    return {
      bundles: [],
      currency: theme_vars.currency,
      locale: theme_vars.locale.replace(/_/g, '-'),
      texts: theme_vars.contactTexts,
      therapyBundleDiscounts:
        theme_vars.therapyBundleDiscounts &&
        theme_vars.therapyBundleDiscounts.length
          ? theme_vars.therapyBundleDiscounts
          : [],
    };
  },

  methods: {

    calculateBundles() {
      if (!this.price) {
        return [];
      }

      return this.therapyBundleDiscounts
        .filter((bundle) => {
          return bundle.count > 1;
        })
        .map(this.parseBundle);
    },
    calculatePrice() {
      if (!this.priceMetaKey) {
        return '';
      }

      if (this.selectedUser) {
        return this.getPriceFromUser();
      } else if (this.pricesList) {
        return this.priceFromPrices();
      }

      return '';
    },

    formatPrice(price) {
      return new Intl.NumberFormat(this.locale, {
        currency: this.currency,
        minimumFractionDigits: price % 1 === 0 ? 0 : 2,
        style: 'currency',
      }).format(price);
    },

    getBundlesFromUser() {
      if (!this.priceMetaKey) {
        return [];
      }

      const bundles = this.bundlesMeta || this.selectedUser?.meta;

      if (!bundles) {
        return [];
      }

      if (this.selectedUser?.meta) {
        return Object.entries(this.selectedUser.meta)
          .filter(([, value]) => value.key.includes('discount_'))
          .map(([, value]) => {
            return {
              count: parseInt(value.key.replace('discount_', '')),
              discoundPercentage: parseInt(value.value.replace(/[^0-9]/g, '')),
            };
          })
          .map(this.parseBundle)
          .sort((a, b) => {
            const order = [3, 5, 10];
            return (
              order.indexOf(Number(a.count)) - order.indexOf(Number(b.count))
            );
          });
      }

      const requiredBundles = [
        'therapy_user_discount_3',
        'therapy_user_discount_5',
        'therapy_user_discount_10',
      ];
      const keyRolePrefix = API_ROLES.THERAPY;

      requiredBundles.forEach((key) => {
        if (!bundles[key]) {
          bundles[key] = '0'; // Default discount percentage if not present
        }
      });

      return Object.entries(bundles)
        .filter(([key]) => key.includes(`${keyRolePrefix}_discount_`))
        .map(([key, value]) => ({
          count: parseInt(
            key.replace(`${keyRolePrefix}_discount_`, '').replace('discount_', '')
          ),
          discoundPercentage: parseInt(value.replace(/[^0-9]/g, '')),
        }))
        .map(this.parseBundle)
        .sort((a, b) => {
          const order = [3, 5, 10];
          return (
            order.indexOf(Number(a.count)) - order.indexOf(Number(b.count))
          );
        });
    },

    getDiscountDescription(count, price) {
      if (this.price === price) {
        return '';
      }

      const text =
        count === 1
          ? this.texts.normalPrice
          : this.texts.discountDescription.replace(
              '%s',
              this.formatPrice(this.price * count - price * count)
            );

      return count === 1 ? `(${text})` : `(<em>${text}</em>)`;
    },

    getMeetingLabel(count, price = this.price) {
      return count === 1
        ? sprintf(this.texts.singleMeeting, this.formatPrice(price))
        : sprintf(this.texts.multipleMeetings, count, this.formatPrice(price));
    },

    getPriceFromUser() {
      if (!this.priceMetaKey) {
        return '';
      }

      const metaKey = this.priceMetaKey.replace(`${API_ROLES.THERAPY}_`, '');
      const priceMeta = Object.values(this.selectedUser.meta).filter(
        (meta) => meta.key === metaKey && hasTherapyRole(meta.role)
      );
      if (priceMeta.length === 0) {
        return '';
      }

      return parseInt(priceMeta[0].value.replace(/[^0-9]/g, ''));
    },

    getSingle() {
      if (!this.price) {
        return null;
      }

      return this.therapyBundleDiscounts
        .filter((bundle) => {
          return bundle.count === 1;
        })
        .map(this.parseBundle)[0];
    },

    parseBundle(bundle) {
      return {
        count: bundle.count,
        discount: bundle.discoundPercentage,
        price: this.price - (this.price * bundle.discoundPercentage) / 100,
      };
    },

    priceFromPrices() {
      if (!this.priceMetaKey) {
        return '';
      }

      if (this.meetingType === MEETING_TYPE_KEYS.AT_OUR_OFFICES) {
        return parseInt(
          this.officePricesList[this.priceMetaKey].replace(/[^0-9]/g, '')
        );
      }

      return parseInt(
        this.pricesList[this.priceMetaKey].replace(/[^0-9]/g, '')
      );
    },

    updateBundles() {
      const single = this.getSingle();
      const userBundles = this.getBundlesFromUser();
      const bundles = userBundles.length
        ? userBundles
        : this.calculateBundles();

      // Sort bundles by count in descending order
      const sortedBundles = [single, ...bundles].sort(
        (a, b) => b.count - a.count
      );

      this.bundles = sortedBundles;
    },
  },

  mounted() {
    this.updateBundles();
  },
  name: 'MeetingBundleSelect',

  props: {
    bundlesMeta: {
      default: null,
      type: Object,
    },
    hasError: {
      default: false,
      type: Boolean,
    },
    meetingCount: {
      default: MEETING_BUNDLE_COUNT,
      type: Number,
    },
    meetingType: {
      default: null,
      type: String,
    },
    officePricesList: {
      default: null,
      type: [Object, Array],
    },
    priceMetaKey: {
      default: null,
      type: String,
    },
    pricesList: {
      default: null,
      type: Object,
    },
    requestType: {
      default: '',
      type: String,
    },
    selectedUser: {
      default: null,
      type: Object,
    },
  },

  watch: {
    priceMetaKey: 'updateBundles',
    pricesList: 'updateBundles',
    selectedUser: 'updateBundles',
  },
};
</script>
